/**
 * Lesson System Type Definitions
 * Types for the lesson browsing, exercise, completion, and spaced repetition systems.
 */

// Re-export locale types used by lesson consumers
export type { SourceLocale, TargetLocale, LessonLanguage, AnyLocale } from './locale';
export { getActiveLocale, getBaseLanguage } from './locale';
import { getDefaultVariants, getDefaultVariant, getBaseLanguage } from './locale';
import type { LessonRecommendedResponse } from '../generated/api/http-contracts';

// ==========================================
// Bilingual Content System
// ==========================================

/**
 * Bilingual content — locale-keyed record of translated strings.
 * Keys use underscores (BCP 47 with hyphens → underscores for JS).
 * The server includes at least the course's default variant keys (e.g., es_419, en_US).
 * Other variant keys (es_MX, en_GB) are optional overrides.
 *
 * Shape is determined by the language course, not hardcoded.
 * Example: { es_419: "Hola", en_US: "Hello", es_MX: "¡Hola!" }
 */
export type BilingualContent = Record<string, string>;

/** Check if a value looks like a BilingualContent object (has at least one default variant key). */
export function isBilingualContent(value: unknown): value is BilingualContent {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  // Check for any default variant key (e.g., 'es_419', 'en_US')
  return Object.values(getDefaultVariants()).some((variant) => variant.replace(/-/g, '_') in record);
}

/** Convert a BCP 47 locale to its BilingualContent property key (hyphens → underscores). */
function toContentKey(locale: string): string {
  return locale.replace(/-/g, '_');
}

/**
 * Resolve the correct language string from bilingual content.
 * Fallback chain: user's variant → default variant for that base → empty string.
 */
export function resolveBilingualContent(
  content: BilingualContent | string,
  lessonLanguage: import('./locale').LessonLanguage,
  sourceLocale: import('./locale').SourceLocale,
  targetLocale: import('./locale').TargetLocale,
): string {
  if (typeof content === 'string') return content;

  const record = content as unknown as Record<string, string | undefined>;
  const locale = lessonLanguage === 'target' ? targetLocale : sourceLocale;
  const variantKey = toContentKey(locale);
  const defaultKey = toContentKey(getDefaultVariant(getBaseLanguage(locale)));

  return record[variantKey] ?? record[defaultKey] ?? '';
}

// ==========================================
// CEFR Levels
// ==========================================

/**
 * The one level axis (ADR-033 D4) — A1–C2, owned by the AsyncAPI contract
 * because it crosses backend↔mobile. Re-exported (not redeclared) so this tree
 * can never drift from the server enum again.
 */
export type { CEFRLevel } from '../generated/api/http-contracts';

// ==========================================
// Lesson Categories (Skill Areas)
// ==========================================

export type LessonCategory =
  | 'grammar'
  | 'vocabulary'
  | 'pronunciation'
  | 'communication'
  | 'spanish-challenges'
  | 'listening'
  | 'writing'
  | 'culture';

// ==========================================
// Exercise Types
// ==========================================

export type ExerciseType =
  | 'fill_blank'
  | 'choose_correct'
  | 'translate'
  | 'repeat_after'
  | 'speak'
  | 'pronunciation_challenge'
  | 'reorder_words'
  | 'match_pairs'
  | 'listen_and_choose'
  | 'dictation'
  | 'error_correction'
  | 'dialogue_completion'
  | 'free_write'
  | 'listen_and_respond';

// ==========================================
// Lesson Path (Flat Navigation)
// ==========================================

export interface LessonPathItem {
  id: string;
  slug: string;
  title: string;
  description?: string;
  level: string;
  category?: string;
  estimatedMins?: number;
  orderIndex?: number;
  completion?: {
    bestScore?: number;
    totalAttempts?: number;
    nextReviewDate?: string;
  } | null;
}

export interface LessonPathSection {
  slug: string;
  title: string;
  category: string;
  lessons: LessonPathItem[];
}

export interface LessonPathData {
  sections: LessonPathSection[];
  continueLesson?: {
    slug?: string;
    title?: string;
    category?: string;
  } | null;
}

// ==========================================
// Lesson Detail (v3 format)
// ==========================================

export interface LessonDetail {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  level: string;
  category: string;
  estimatedMins: number | null;
  isPublished: boolean;
  content: Record<string, unknown>;
  exercises: LessonExercise[];
  deprecated?: {
    at?: string;
    replacedBy?: {
      id?: string;
      slug?: string;
      title?: string;
    };
  };
}

export interface LessonContent {
  sections: LessonSection[];
}

export interface LessonSection {
  type: 'explanation' | 'tip' | 'practice' | 'practice_ai' | 'summary';
  title?: BilingualContent | string;
  body?: BilingualContent | string;
  exercises?: LessonExerciseContent[];
  aiAgentInstructions?: AiAgentInstructions;
}

export interface LessonExerciseContent {
  type: ExerciseType;
  prompt: BilingualContent | string;
  correctAnswer?: string;
  acceptableAnswers?: string[];
  options?: string[];
  hint?: BilingualContent | string;
  words?: string[];
  correctOrder?: string[];
  pairs?: Array<{ left: string; right: string }>;
  targetPhrase?: string;
  targetWord?: string;
  phoneticGuide?: string;
  commonMistake?: BilingualContent | string;
  audioText?: string;
  expectedKeywords?: string[];
  voiceEnabled?: boolean;
  minScore?: number;
  // dictation
  wordCount?: number;
  speed?: 'slow' | 'normal' | 'fast';
  // error_correction
  sentence?: string;
  errors?: Array<{
    wordIndex: number;
    incorrect: string;
    correct: string;
    acceptableCorrections?: string[];
  }>;
  explanation?: BilingualContent | string;
  errorCategory?: string;
  // dialogue_completion
  dialogue?: Array<{
    speaker: string;
    text: string | null;
    isBlank: boolean;
    audioText?: string;
  }>;
  correctAnswers?: string[];
  context?: BilingualContent | string;
  register?: 'formal' | 'informal' | 'neutral';
  // free_write
  writingType?: 'email' | 'paragraph' | 'description' | 'message' | 'story';
  minWords?: number;
  maxWords?: number;
  rubric?: {
    grammar: number;
    vocabulary: number;
    coherence: number;
    taskCompletion: number;
  };
  sampleAnswer?: string;
  scaffolding?: Array<BilingualContent | string>;
  targetVocabulary?: string[];
  // listen_and_respond
  passage?: string;
  passageTitle?: BilingualContent | string;
  questions?: Array<{
    question: BilingualContent | string;
    options: string[];
    correctAnswer: string;
    explanation?: BilingualContent | string;
  }>;
  showTranscript?: boolean;
}

export interface LessonExercise {
  id: string;
  type: string;
  title?: string | null;
  instructions?: string | null;
  content?: Record<string, unknown>;
  orderIndex: number;
  points?: number | null;
}

// ==========================================
// AI Exercise Generation
// ==========================================

export interface AiAgentInstructions {
  count: number;
  exerciseTypes: string[];
  topic: string;
  level: string;
  constraints: string;
  personalize?: boolean;
  freshOnEveryVisit?: boolean;
}

export interface GeneratedExercise {
  type: ExerciseType;
  prompt: BilingualContent;
  correctAnswer: string;
  options?: string[];
  hint?: BilingualContent;
  acceptableAnswers?: string[];
  voiceEnabled?: boolean;
}

// ==========================================
// Exercise Progress & Results
// ==========================================

export interface ExerciseProgress {
  completed: boolean;
  correct: boolean;
  score: number;
  type: ExerciseType;
  voiceUsed: boolean;
  userAnswer?: string;
  userOrder?: string[];
  matchedPairs?: number;
}

export interface ExerciseResult {
  correct: boolean;
  score: number;
  type: string;
  voiceUsed: boolean;
}

// ==========================================
// Lesson Completion
// ==========================================

export interface CompletionResult {
  id: string;
  score: number;
  attemptNumber: number;
  nextReviewDate: string;
  /** @deprecated Not in API response — kept for UI backward compat */
  completionId?: string;
  /**
   * True on the first attempt and whenever the score beats every prior one.
   * Optional because a client running ahead of the backend during a staged
   * rollout may not receive it; absence renders no badge.
   */
  isNewHighScore?: boolean;
  /** Max score across prior attempts; null on the first attempt. */
  previousBestScore?: number | null;
}

export interface CompletionHistory {
  lessonSlug: string;
  lessonTitle: string;
  completions: Array<{
    id: string;
    score: number;
    attemptNumber: number;
    completedAt: string;
    exerciseResults: Record<string, { correct: boolean; score: number }>;
  }>;
  bestScore: number | null;
  totalAttempts: number;
  nextReviewDate: string | null;
}

// ==========================================
// Recommended Lessons
// ==========================================

/**
 * One entry from GET /api/lessons/recommended: a lesson plus the reason it
 * was picked — the error type the learner keeps making, and how often.
 *
 * Derived from the contract rather than hand-written; the previous local
 * interface had drifted (it declared `priority`/`level`/`category` the
 * endpoint never returns, and omitted `errorCount`, which it does).
 */
export type RecommendedLesson = LessonRecommendedResponse['lessons'][number];

// ==========================================
// Lesson Search
// ==========================================

export interface LessonSearchResult {
  lesson: {
    id: string;
    slug: string;
    title: string;
    level: string;
    category: string;
    description: string | null;
  };
  matchedKeywords: string[];
  relevanceScore: number;
}

// ==========================================
// Lesson List Filters
// ==========================================

export interface LessonListFilters {
  level?: string;
  category?: string;
  limit?: number;
  offset?: number;
}

export interface LessonSearchFilters {
  keywords: string[];
  level?: string;
  category?: string;
  excludeSlugs?: string[];
  limit?: number;
}

// ==========================================
// Quick Practice (error-targeted micro-lessons)
// ==========================================

export interface QuickPracticeExample {
  native: { es_419: string; es_ES?: string; es_MX?: string };
  target: string;
  highlight?: string[];
  note?: { es_419: string; en_US: string };
}

export interface QuickPracticeExercise {
  type: string;
  prompt: { es_419: string; en_US: string };
  correctAnswer: string;
  options?: string[];
  hint?: { es_419: string; en_US: string };
  acceptableAnswers?: string[];
}

export interface QuickPracticeContent {
  explanation: { es_419: string; en_US: string };
  examples: QuickPracticeExample[];
  exercises: QuickPracticeExercise[];
}
